import { Mic, Stop } from '@mui/icons-material'
import { Box, Button, CircularProgress, IconButton, Stack, Typography } from '@mui/material'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'
import { useUploadVoiceAnswerMutation } from '~/app/store/services'
import { cancelNarration, getNarrationLanguage, speak, t } from '~/utils/narration'

const pickMimeType = () => {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg']
  for (const type of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)) return type
  }
  return ''
}

const MAX_RECORDING_SECONDS = 30

type Props = {
  onContinue: () => void
  id: number
  questionKey: string
  titleEn: string
  titleHi?: string
}

export default function VoiceQuestion({ onContinue, id, questionKey, titleEn, titleHi }: Props) {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tickTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const startedRef = useRef(false)
  const [recording, setRecording] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const [isProcessing, setProcessing] = useState(false)
  const [answer, setAnswer] = useState<string | null>(null)
  const [answerHi, setAnswerHi] = useState<string | null>(null)
  const [uploadVoiceAnswer] = useUploadVoiceAnswerMutation()

  const clearTimers = () => {
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current)
    if (tickTimerRef.current) clearInterval(tickTimerRef.current)
    stopTimerRef.current = null
    tickTimerRef.current = null
  }

  const releaseMic = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }

  useEffect(() => {
    speak({ en: titleEn, hi: titleHi }, () => {
      if (!startedRef.current) startRecording()
    })
    return () => {
      cancelNarration()
      clearTimers()
      releaseMic()
    }
  }, [])

  const handleUpload = async (mimeType: string) => {
    try {
      if (!chunksRef.current.length) throw new Error('No audio captured')
      setProcessing(true)
      const type = mimeType || 'audio/webm'
      const blob = new Blob(chunksRef.current, { type })
      const ext = type.includes('mp4') ? 'mp4' : type.includes('ogg') ? 'ogg' : 'webm'

      const formData = new FormData()
      formData.append('audio', blob, `answer.${ext}`)
      formData.append('question_key', questionKey)
      formData.append('lang', getNarrationLanguage())

      const res = await uploadVoiceAnswer({ id, formData }).unwrap()
      setAnswer(res?.answer || '')
      setAnswerHi(res?.answer_hi || null)
    } catch (error) {
      console.log('voice answer failed', error)
      startedRef.current = false
      chunksRef.current = []
      setElapsed(0)
      enqueueSnackbar(t({ en: 'Could not process your answer. Please try again.', hi: 'आपका उत्तर प्रोसेस नहीं हो सका। कृपया पुनः प्रयास करें।' }), {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      })
    } finally {
      setProcessing(false)
    }
  }

  const startRecording = async () => {
    if (startedRef.current) return
    startedRef.current = true
    try {
      cancelNarration()
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      chunksRef.current = []

      const mimeType = pickMimeType()
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
      mediaRecorderRef.current = recorder
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data)
      }
      recorder.onstop = () => {
        releaseMic()
        handleUpload(recorder.mimeType)
      }
      recorder.start()
      setRecording(true)
      setElapsed(0)
      tickTimerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000)
      stopTimerRef.current = setTimeout(() => stopRecording(), MAX_RECORDING_SECONDS * 1000)
    } catch (error) {
      startedRef.current = false
      console.log('mic error', error)
      enqueueSnackbar(t({ en: 'Unable to access microphone. Please allow mic permission.', hi: 'माइक्रोफ़ोन चालू नहीं हो पाया। कृपया माइक की अनुमति दें।' }), {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      })
    }
  }

  const stopRecording = () => {
    clearTimers()
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
    setRecording(false)
  }

  const handleReRecord = () => {
    setAnswer(null)
    setAnswerHi(null)
    setElapsed(0)
    chunksRef.current = []
    startedRef.current = false
  }

  return (
    <Stack spacing={3} alignItems="center" justifyContent="center" minHeight="70vh" px={2}>
      <Typography variant="h5" fontWeight={700}>
        {t({ en: titleEn, hi: titleHi })}
      </Typography>

      {isProcessing ? (
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography>{t({ en: 'Transcribing your answer...', hi: 'आपका उत्तर लिखा जा रहा है...' })}</Typography>
        </Stack>
      ) : answer !== null ? (
        <Stack spacing={3} alignItems="center" width="100%">
          <Box
            sx={{
              bgcolor: '#fff',
              borderRadius: 2,
              p: 2,
              width: '100%',
              maxWidth: 480,
              boxShadow: 1,
              textAlign: 'left',
            }}
          >
            {getNarrationLanguage() === 'hi' && answerHi && (
              <>
                <Typography variant="overline" color="text.secondary">
                  आपका उत्तर
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>{answerHi}</Typography>
              </>
            )}
            <Typography variant="overline" color="text.secondary">
              {t({ en: 'Your answer (English)', hi: 'आपका उत्तर (अंग्रेज़ी)' })}
            </Typography>
            <Typography variant="body1">{answer || '—'}</Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={handleReRecord}>
              {t({ en: 'Re-record', hi: 'फिर से रिकॉर्ड करें' })}
            </Button>
            <Button variant="contained" onClick={onContinue}>
              {t({ en: 'Continue', hi: 'आगे बढ़ें' })}
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Stack spacing={2} alignItems="center">
          <Typography color="text.secondary">
            {recording
              ? t({
                  en: `Recording… ${elapsed}s / ${MAX_RECORDING_SECONDS}s — tap to stop when you are done.`,
                  hi: `रिकॉर्डिंग… ${elapsed}s / ${MAX_RECORDING_SECONDS}s — पूरा होने पर रोकने के लिए टैप करें।`,
                })
              : t({
                  en: `Tap the mic and answer in any language (max ${MAX_RECORDING_SECONDS}s).`,
                  hi: `माइक पर टैप करें और किसी भी भाषा में उत्तर दें (अधिकतम ${MAX_RECORDING_SECONDS}s)।`,
                })}
          </Typography>
          <IconButton
            onClick={recording ? stopRecording : startRecording}
            sx={{
              width: 96,
              height: 96,
              color: '#fff',
              bgcolor: recording ? 'error.main' : 'primary.main',
              '&:hover': { bgcolor: recording ? 'error.dark' : 'primary.dark' },
            }}
          >
            {recording ? <Stop sx={{ fontSize: 44 }} /> : <Mic sx={{ fontSize: 44 }} />}
          </IconButton>
        </Stack>
      )}
    </Stack>
  )
}
